import type { AxiosInstance } from "axios";

import type {
  AssessmentId,
  FormDraft,
  FormProgress,
  OpenGroupRequest,
  PreviousCommentsAnswer,
  SaveFormDraftRequest,
  StartFormDraftRequest,
  SubmissionRecorded,
  SubmitFormDraftRequest,
} from "@thalamos/haven-common";
import {
  FormDraftSchema,
  FormProgressSchema,
  PreviousCommentsAnswerSchema,
  SubmissionRecordedSchema,
} from "@thalamos/haven-common";

import type { ErrorBody } from "../axios/api.js";

const draftUrl = (assessmentId: AssessmentId) =>
  `/api/collectors/form/assessments/${assessmentId}/draft`;

/**
 * An answer from the form's routes: the parsed body where the status is the
 * one that carries it, and the error body otherwise, so a caller reads which it
 * got without a cast.
 */
export type FormDraftResponse<T> =
  | { status: number; data: T; error?: undefined }
  | { status: number; data?: undefined; error: ErrorBody };

const parsedOn =
  <T>(status: number, parse: (data: unknown) => T) =>
  (response: { status: number; data: unknown }): FormDraftResponse<T> =>
    response.status === status
      ? { status: response.status, data: parse(response.data) }
      : { status: response.status, error: response.data as ErrorBody };

const parseDraft = (data: unknown): FormDraft => FormDraftSchema.parse(data);
const parseRecorded = (data: unknown): SubmissionRecorded =>
  SubmissionRecordedSchema.parse(data);

/** The first-party form's routes for an assessment: its draft, and submitting it. */
export const config = {
  formDraft: {
    create: (assessmentId: AssessmentId, start?: StartFormDraftRequest) => ({
      url: draftUrl(assessmentId),
      method: "post",
      data: start ?? {},
    }),
    resume: (assessmentId: AssessmentId) => ({
      url: draftUrl(assessmentId),
      method: "get",
    }),
    save: (assessmentId: AssessmentId, body: SaveFormDraftRequest) => ({
      url: draftUrl(assessmentId),
      method: "put",
      data: body,
    }),
    previousComments: (assessmentId: AssessmentId) => ({
      url: `${draftUrl(assessmentId)}/previous-comments`,
      method: "get",
    }),
    progress: (assessmentId: AssessmentId) => ({
      url: `/api/collectors/form/assessments/${assessmentId}/progress`,
      method: "get",
    }),
    openGroup: (assessmentId: AssessmentId, body: OpenGroupRequest) => ({
      url: `${draftUrl(assessmentId)}/opened-groups`,
      method: "post",
      data: body,
    }),
    submit: (assessmentId: AssessmentId, body: SubmitFormDraftRequest) => ({
      url: `/api/collectors/form/assessments/${assessmentId}/submissions`,
      method: "post",
      data: body,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => ({
  formDraft: {
    /**
     * Starts a draft, empty or from one of the assessment's submissions, which
     * reopens a returned one; 409 where the assessment already has a draft.
     */
    create: async (assessmentId: AssessmentId, start?: StartFormDraftRequest) =>
      parsedOn(
        201,
        parseDraft,
      )(
        await apiAxios.request<unknown>(
          config.formDraft.create(assessmentId, start),
        ),
      ),
    /** The draft as last saved, with facts to judge it against now; 404 where there is none. */
    resume: async (assessmentId: AssessmentId) =>
      parsedOn(
        200,
        parseDraft,
      )(await apiAxios.request<unknown>(config.formDraft.resume(assessmentId))),
    save: async (assessmentId: AssessmentId, body: SaveFormDraftRequest) =>
      parsedOn(
        200,
        parseDraft,
      )(
        await apiAxios.request<unknown>(
          config.formDraft.save(assessmentId, body),
        ),
      ),
    /** Last time's comments on the draft's items, where ratings were carried over. */
    previousComments: async (assessmentId: AssessmentId) =>
      parsedOn(200, (data): PreviousCommentsAnswer =>
        PreviousCommentsAnswerSchema.parse(data),
      )(
        await apiAxios.request<unknown>(
          config.formDraft.previousComments(assessmentId),
        ),
      ),
    /** How many of the assessment's domains are completed, for whoever may delete it. */
    progress: async (assessmentId: AssessmentId) =>
      parsedOn(200, (data): FormProgress => FormProgressSchema.parse(data))(
        await apiAxios.request<unknown>(
          config.formDraft.progress(assessmentId),
        ),
      ),
    /** Records that one of the draft's groups has been opened; 204 once it is. */
    openGroup: async (assessmentId: AssessmentId, body: OpenGroupRequest) =>
      parsedOn(
        204,
        () => undefined,
      )(
        await apiAxios.request<unknown>(
          config.formDraft.openGroup(assessmentId, body),
        ),
      ),
    submit: async (assessmentId: AssessmentId, body: SubmitFormDraftRequest) =>
      parsedOn(
        201,
        parseRecorded,
      )(
        await apiAxios.request<unknown>(
          config.formDraft.submit(assessmentId, body),
        ),
      ),
  },
});
